/**
 * Zugriff auf das eigene Backend.
 *
 * Das Frontend spricht ausschließlich mit dem eigenen Backend (SECURITY.md §1);
 * es gibt hier keine Aufrufe an Dritte und keine Tokens im Browser. Die Sitzung
 * steckt im `HttpOnly`-Cookie und wird von `credentials: 'same-origin'`
 * mitgeschickt, ohne dass JavaScript sie je zu sehen bekommt.
 */

import { type Fehler, RECHTESTAND_KOPF } from '@nac2s/shared'

/**
 * Fehlerformat des Backends (SECURITY.md §2).
 *
 * Steht in `packages/shared`, weil es jede Oberflaeche und jeder Klient
 * auswertet — eine zweite Beschreibung waere eine zweite Wahrheit.
 */
export type ApiErrorBody = Fehler

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Nicht angemeldet — die Oberfläche verweist dann auf den Client. */
  get istNichtAngemeldet(): boolean {
    return this.status === 401
  }
}

async function parseError(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as Partial<ApiErrorBody> & { message?: string }
    // Der Zweig /api/auth gehört Better Auth und antwortet im eigenen Format
    // (securitytodo.md ST-024) — beide Formen werden hier zusammengeführt.
    const message = body.error ?? body.message ?? 'Unbekannter Fehler'
    return new ApiError(response.status, body.code ?? 'unknown', message)
  } catch {
    return new ApiError(response.status, 'unknown', 'Der Dienst hat unerwartet geantwortet.')
  }
}

/**
 * Der Rechtestand aus der letzten Antwort.
 *
 * Jede beantwortete Anfrage traegt ihn (`x-nadia-rechtestand`). Aendert der
 * Partner Rollen oder Stufe, ist er ein anderer — und die Sitzungsverwaltung
 * liest nach, ohne dass jemand die Seite neu laedt. Damit wirkt eine
 * Aenderung bei der naechsten Handlung und nicht erst im Takt.
 */
let melden: ((stand: string) => void) | null = null

/** Meldet jeden Rechtestand, den eine Antwort mitbringt. Gibt das Abmelden zurueck. */
export function beiRechtestand(zuhoerer: (stand: string) => void): () => void {
  melden = zuhoerer
  return () => {
    if (melden === zuhoerer) melden = null
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response
  try {
    response = await fetch(path, {
      ...init,
      credentials: 'same-origin',
      headers: {
        ...(init.body ? { 'content-type': 'application/json' } : {}),
        ...init.headers,
      },
    })
  } catch {
    // Netzwerkfehler sind für den Anwender dasselbe wie ein toter Dienst.
    throw new ApiError(0, 'unreachable', 'Der Dienst ist nicht erreichbar.')
  }

  const stand = response.headers.get(RECHTESTAND_KOPF)
  if (stand) melden?.(stand)

  if (!response.ok) throw await parseError(response)
  if (response.status === 204) return undefined as T

  return (await response.json()) as T
}
